import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** English mirror of the Thai apostille-vs-legalisation guide. */
const TITLE = "Apostille vs Legalisation for Thai Documents — Country-by-Country Guide";
const DESC =
  "How Thai documents are certified for use abroad: the difference between apostille and consular legalisation, the correct order of steps, and country-specific notes.";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/en${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "What is the practical difference between an apostille and legalisation?",
    answer:
      "An apostille is a single certificate issued under the Hague Apostille Convention that member states accept without any further embassy step. Consular legalisation is the older chain: the document is certified by the issuing authority, then by the foreign ministry of the issuing country, and finally by the embassy or consulate of the destination country. The paperwork looks similar, but the number of counters you visit is different.",
  },
  {
    question: "Which route applies to a Thai document today?",
    answer:
      "It depends on both the destination country and the date the destination authority receives the document. Always confirm the current requirement with the destination authority or its embassy before you start, because the accepted route determines the whole sequence and cannot be corrected later without redoing certifications.",
  },
  {
    question: "Does the translation come before or after certification?",
    answer:
      "In most cases the Thai original is certified first, then translated, and the translation itself is certified. Reversing that order is one of the most common reasons a file is returned, because the certification stamp on the original will not appear in the translated set.",
  },
  {
    question: "Who may translate the document?",
    answer:
      "The destination authority decides. Some accept a translation certified by the Thai Ministry of Foreign Affairs Legalisation Division, some require a translator registered with a local court or professional body, and Australian authorities generally require a NAATI-certified translator. Choosing the translator before checking the destination rule is a frequent cause of rework.",
  },
  {
    question: "Can copies be used instead of originals?",
    answer:
      "Certification is normally applied to originals or to copies certified by the issuing authority. A plain photocopy is almost always rejected, and a scanned reproduction of a certified set is not a substitute unless the destination authority states that it accepts electronic copies.",
  },
  {
    question: "How long is a certified document valid?",
    answer:
      "The certificate itself usually has no expiry date, but destination authorities commonly require documents issued within three or six months of submission. Plan backwards from the submission date rather than certifying documents far in advance.",
  },
  {
    question: "What are the most common reasons for rejection?",
    answer:
      "Name spellings that differ from the passport, incomplete translations that omit text inside seals and signatures, steps performed in the wrong order, missing intermediate certifications, and documents issued outside the destination authority's validity window.",
  },
];

export const Route = createFileRoute("/en/blog/apostille-vs-legalization-by-country")({
  component: EnApostilleVsLegalisation,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "en" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "en") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "apostille thailand, thai document legalisation, mfa legalisation bangkok, embassy legalisation thai documents, certified translation thailand",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "en"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: "/en" },
              { name: "Blog", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const STEPS: Array<{ title: string; detail: string }> = [
  {
    title: "1) Confirm the destination requirement in writing",
    detail:
      "Ask the destination authority which route it accepts, which language the translation must be in, and how recent the document must be. This single answer determines every later step.",
  },
  {
    title: "2) Obtain a clean original from the issuing authority",
    detail:
      "Registry extracts, police certificates, and academic records must be current issues, with legible seals. A worn or annotated copy will fail at the first certification counter.",
  },
  {
    title: "3) Have signatures notarised where required",
    detail:
      "Private documents such as powers of attorney, affidavits, and company resolutions need a notarial act before any government certification. Public records issued by a Thai authority skip this step.",
  },
  {
    title: "4) Certify the Thai original",
    detail:
      "The Legalisation Division of the Department of Consular Affairs certifies the signature and seal of the Thai issuing authority. This is the anchor that makes the document verifiable abroad.",
  },
  {
    title: "5) Translate and certify the translation",
    detail:
      "Translate every visible element, including text inside stamps and handwritten notes, keep passport spellings exactly, and write calendar years unambiguously. Then certify the translation itself.",
  },
  {
    title: "6) Complete the destination-side step",
    detail:
      "Either collect the apostille where the destination accepts it, or submit the certified set to the destination embassy or consulate for legalisation. Never reverse steps 4 to 6.",
  },
];

const COUNTRY_NOTES: Array<{ country: string; note: string }> = [
  {
    country: "Australia",
    note: "Immigration and academic bodies typically expect a NAATI-certified translation. Confirm whether the original also needs Thai government certification before the translation is produced.",
  },
  {
    country: "United States",
    note: "Requirements vary by receiving institution and by state. Universities, county clerks, and federal agencies do not share one standard, so ask the specific recipient.",
  },
  {
    country: "China",
    note: "Chinese authorities are strict about complete translations and about the order of certifications. Verify the current requirement with the Chinese embassy or consulate handling your province.",
  },
  {
    country: "Japan and Korea",
    note: "Both commonly accept Thai documents certified by the Department of Consular Affairs followed by the destination embassy step. Confirm the validity window, which is often three months.",
  },
  {
    country: "European countries",
    note: "Many are Hague Convention members, so an apostille route may apply. Check whether the receiving institution requires a translation certified in its own country instead.",
  },
  {
    country: "Gulf states",
    note: "Employment and marriage files frequently require an additional embassy step and a specific translation format. Ask for the checklist before starting, as formats change.",
  },
];

const MISTAKES: string[] = [
  "Translating before the Thai original is certified, so the certification never appears in the translated set",
  "Using a photocopy where the authority requires an original or an authority-certified copy",
  "Leaving text inside seals and signature blocks untranslated",
  "Spelling names differently across documents in the same file",
  "Certifying documents months early and missing the destination validity window",
  "Assuming one country's checklist applies to another destination",
];

function EnApostilleVsLegalisation() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/en" className="text-muted-foreground hover:text-foreground">
          Home
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Blog
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="en" />
      <p className="answer-summary speakable-summary">
        Apostille and consular legalisation solve the same problem — proving to a foreign authority
        that a Thai document is genuine — but they use a different number of steps. The apostille
        route ends with one certificate accepted by Hague Convention members; legalisation ends at
        the destination country's embassy. What determines your route is the destination authority,
        not the document type, so confirm that first and then build the sequence backwards from your
        submission date.
      </p>

      <h2>1. Why the destination authority decides everything</h2>
      <p>
        A receiving authority is not asking for stamps for their own sake. It needs a chain it can
        verify: the issuer is real, the signature is real, and the translation matches the original.
        Once you know which chain the destination recognises, the order of steps stops being
        guesswork. When people skip this question, they usually pay twice — once for the wrong
        chain, and once again for the correct one.
      </p>

      <h2>2. The six-step sequence that fits nearly every file</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s.title}>
            <strong>{s.title}</strong> {s.detail}
          </li>
        ))}
      </ol>

      <h2>3. Country notes</h2>
      <p>
        These notes describe patterns we see repeatedly in practice. They are a starting point for
        planning, not a substitute for the destination authority's current instructions.
      </p>
      <ul>
        {COUNTRY_NOTES.map((c) => (
          <li key={c.country}>
            <strong>{c.country}:</strong> {c.note}
          </li>
        ))}
      </ul>

      <h2>4. Mistakes that force a restart</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. Planning the timeline</h2>
      <p>
        Fix the destination submission date first, then count backwards through the embassy step, the
        certification of the translation, the translation itself, the certification of the original,
        and the time needed to obtain a fresh original. Each step depends on the previous one, so a
        delay early in the chain moves the whole plan. Keep a spare certified set where the
        destination authority does not return submitted documents.
      </p>

      <h2>6. Frequently asked questions</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>7. References and related services</h2>
      <p className="text-sm">
        References:{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          HCCH — Apostille Section
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/en/lgz" className="text-primary underline">
            Document legalisation service
          </Link>
        </li>
        <li>
          <Link to="/en/notary" className="text-primary underline">
            Notarial services by Thai lawyers
          </Link>
        </li>
        <li>
          <Link to="/en/blog/naati-vs-court-registered-translator" className="text-primary underline">
            NAATI vs court-registered translators
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Content reviewed on {PUBLISHED}. This guide is general information for planning purposes and
        is not a determination made on behalf of any authority. Please confirm current requirements
        with the destination authority before you proceed.
      </p>
    </article>
  );
}
